import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TableFooter,
  Paper,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
} from "@mui/material";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";

export default function DailyPurchaseSummary() {
  const formatDate = (date) => date.toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return formatDate(date);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [groupedDetails, setGroupedDetails] = useState({});

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("title"); // ตั้งค่าเริ่มต้นให้นิ่งขึ้น

  const [selectedItemKey, setSelectedItemKey] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterText, setFilterText] = useState("");

  const cellStyle = { border: "1px solid #ddd" };

  const selectedItem = rows.find((r) => r.key === selectedItemKey);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await services.getPurchaseTransactions({
          status: "payed",
          startDate,
          endDate,
        });
        const purchaseTrans = res.data?.data || [];

        const itemMap = {};
        const detailsMap = {};

        purchaseTrans.forEach((trans) => {
          (trans.items || []).forEach((item) => {
            const weight = Number(item.netWeight || item.weight || 0);
            if (!item || weight <= 0) return;

            const productId = item.productId ?? item.product?.id ?? item.id;
            const key = `${productId}`;
            const unitPrice = Number(item.pricePerUnit || 0);
            const amount = weight * unitPrice;

            if (!itemMap[key]) {
              itemMap[key] = {
                key,
                code: item.product?.code ?? "",
                title: item.product?.title || item.productName || "-",
                category: item.product?.category?.name || "-",
                sub_category: item.product?.subCategory?.name || "-",
                unit: "กก.",
                totalWeight: 0,
                totalAmount: 0,
                totalCost: 0,
                count: 0,
              };
              detailsMap[key] = [];
            }

            itemMap[key].totalWeight += weight;
            itemMap[key].totalAmount += amount;
            itemMap[key].totalCost += unitPrice;
            itemMap[key].count += 1;

            detailsMap[key].push({
              ...item,
              poNumber: trans.billNumber,
              poDate: trans.createdAt,
              supplier: trans.customer?.name || "-",
              netWeight: weight,
              pricePerUnit: unitPrice,
              amountBeforeVat: amount,
            });
          });
        });

        const grouped = Object.values(itemMap).map((item) => ({
          ...item,
          weight: item.totalWeight,
          average_cost:
            item.totalWeight > 0 ? item.totalAmount / item.totalWeight : 0,
          amount_before_vat: item.totalAmount,
        }));

        setRows(grouped);
        setGroupedDetails(detailsMap);
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getComparator = (order, orderBy) =>
    order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);

  const descendingComparator = (a, b, orderBy) => {
    const av = a?.[orderBy];
    const bv = b?.[orderBy];
    if (typeof av === "number" && typeof bv === "number") {
      if (bv < av) return -1;
      if (bv > av) return 1;
      return 0;
    }
    const as = (av ?? "").toString();
    const bs = (bv ?? "").toString();
    return bs.localeCompare(as, "th");
  };

  const sortData = (array, comparator) => {
    const stabilized = array.map((el, index) => [el, index]);
    stabilized.sort((a, b) => {
      const cmp = comparator(a[0], b[0]);
      if (cmp !== 0) return cmp;
      return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
  };

  const formatNum = (value) =>
    new Intl.NumberFormat("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));

  function exportToCSV(data, filename = "data.csv") {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const filteredRows = rows.filter((row) => {
    const term = (filterText || "").toLowerCase();
    return (
      (row.title || "").toLowerCase().includes(term) ||
      ((row.code ?? "") + "").toLowerCase().includes(term) ||
      (row.category || "").toLowerCase().includes(term) ||
      (row.sub_category || "").toLowerCase().includes(term)
    );
  });

  const summary = useMemo(() => {
    const totalWeight = filteredRows.reduce(
      (acc, r) => acc + (r.weight || 0),
      0
    );
    const totalAmountBeforeVat = filteredRows.reduce(
      (acc, r) => acc + (r.amount_before_vat || 0),
      0
    );
    const weightedAvgCost =
      totalWeight > 0 ? totalAmountBeforeVat / totalWeight : 0;

    return {
      count: filteredRows.length,
      totalWeight,
      weightedAvgCost,
      totalAmountBeforeVat,
    };
  }, [filteredRows]);

  return (
    <Box
      sx={{
        p: 2,
        width: "100%",
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
      }}
    >
      <AppBreadcrumbs />

      <Box display="flex" justifyContent="flex-end" gap={2} my={2}>
        <TextField
          label="ค้นหา"
          variant="outlined"
          size="large"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          sx={{ width: "400px" }}
          placeholder="ค้นหาด้วยชื่อสินค้าหรือหมวดหมู่"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="เริ่มต้น"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="สิ้นสุด"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            my={2}
          >
            <Typography variant="h6" gutterBottom>
              สรุปยอดซื้อสินค้ารายชนิด
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              size="large"
              onClick={() => {
                const transformedData = rows.map((row) => ({
                  รหัสสินค้า: row.code,
                  ชื่อสินค้า: row.title,
                  หมวดหมู่หลัก: row.category,
                  หมวดหมู่ย่อย: row.sub_category,
                  "น้ำหนัก (กก.)": (row.weight ?? 0).toFixed(2),
                  หน่วย: "กก.",
                  ต้นทุนเฉลี่ย: (row.average_cost ?? 0).toFixed(2),
                  ราคารวมก่อนภาษี: (row.amount_before_vat ?? 0).toFixed(2),
                }));
                exportToCSV(transformedData, "summary.csv");
              }}
            >
              ข้อมูลหลัก
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell sx={cellStyle}></TableCell>
                  <TableCell sx={{ ...cellStyle, color: "primary.main" }}>
                    <TableSortLabel
                      active={orderBy === "code"}
                      direction={orderBy === "code" ? order : "asc"}
                      onClick={() => handleSort("code")}
                    >
                      รหัสสินค้า
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, color: "primary.main" }}>
                    <TableSortLabel
                      active={orderBy === "title"}
                      direction={orderBy === "title" ? order : "asc"}
                      onClick={() => handleSort("title")}
                    >
                      สินค้า
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, color: "primary.main" }}>
                    <TableSortLabel
                      active={orderBy === "category"}
                      direction={orderBy === "category" ? order : "asc"}
                      onClick={() => handleSort("category")}
                    >
                      หมวดหมู่หลัก
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, color: "primary.main" }}>
                    <TableSortLabel
                      active={orderBy === "sub_category"}
                      direction={orderBy === "sub_category" ? order : "asc"}
                      onClick={() => handleSort("sub_category")}
                    >
                      หมวดหมู่ย่อย
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                  >
                    <TableSortLabel
                      active={orderBy === "weight"}
                      direction={orderBy === "weight" ? order : "asc"}
                      onClick={() => handleSort("weight")}
                    >
                      น้ำหนัก
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={cellStyle}>หน่วย</TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                  >
                    <TableSortLabel
                      active={orderBy === "average_cost"}
                      direction={orderBy === "average_cost" ? order : "asc"}
                      onClick={() => handleSort("average_cost")}
                    >
                      ต้นทุนเฉลี่ย
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                  >
                    <TableSortLabel
                      active={orderBy === "amount_before_vat"}
                      direction={
                        orderBy === "amount_before_vat" ? order : "asc"
                      }
                      onClick={() => handleSort("amount_before_vat")}
                    >
                      ราคารวมก่อนภาษี
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sortData(filteredRows, getComparator(order, orderBy)).map(
                  (row) => (
                    <TableRow key={row.key}>
                      <TableCell sx={{ ...cellStyle, paddingBlock: 0 }}>
                        <IconButton
                          onClick={() => {
                            setSelectedItemKey(row.key);
                            setModalOpen(true);
                          }}
                        >
                          <TroubleshootIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell sx={cellStyle}>{row.code}</TableCell>
                      <TableCell sx={cellStyle}>{row.title}</TableCell>
                      <TableCell sx={cellStyle}>{row.category}</TableCell>
                      <TableCell sx={cellStyle}>{row.sub_category}</TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNum(row.weight)}
                      </TableCell>
                      <TableCell sx={cellStyle}>{row.unit}</TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNum(row.average_cost)}
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNum(row.amount_before_vat)}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>

              <TableFooter>
                <TableRow
                  sx={{
                    backgroundColor: "#fafafa",
                    "& td": { fontSize: "14px", fontWeight: "bold" },
                  }}
                >
                  <TableCell sx={cellStyle} colSpan={5}>
                    ยอดรวม
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.totalWeight)}
                  </TableCell>
                  <TableCell sx={cellStyle}></TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.weightedAvgCost)}
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.totalAmountBeforeVat)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>

          {/* โมดอลรายละเอียด */}
          <Dialog
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            maxWidth="xl"
            fullWidth
          >
            <DialogTitle>
              {selectedItem
                ? `${selectedItem.code ?? ""} ${selectedItem.title ?? ""}`
                : "รายละเอียดรายการสั่งซื้อ"}
            </DialogTitle>
            <DialogContent>
              {`หมายเหตุ: น้ำหนักและราคารวมที่แสดงเป็นสัดส่วนของ ${
                selectedItem && selectedItem.title
              } เท่านั้น`}
              {selectedItemKey && groupedDetails[selectedItemKey] && (
                <>
                  <Box display="flex" justifyContent="flex-end" mb={1}>
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      size="large"
                      onClick={() => {
                        const data = groupedDetails[selectedItemKey] || [];
                        const transformedData = data.map((item) => ({
                          วัน: new Date(item.poDate).toLocaleString("th-TH", {
                            year: "numeric",
                            month: "long",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          }),
                          เลขที่Batch: item.poNumber,
                          ซัพพลายเออร์: item.supplier,
                          "น้ำหนัก (กก.)": (item.netWeight ?? 0).toFixed(2),
                          ราคาต่อหน่วย: (item.pricePerUnit ?? 0).toFixed(2),
                          "รวมก่อนภาษี (บาท)": (
                            item.amountBeforeVat ??
                            (item.netWeight || 0) * (item.pricePerUnit || 0)
                          ).toFixed(2),
                        }));
                        exportToCSV(
                          transformedData,
                          `details-${selectedItemKey}.csv`
                        );
                      }}
                    >
                      ข้อมูลย่อย
                    </Button>
                  </Box>

                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                        <TableCell sx={cellStyle}>วันและเวลาจ่ายBatch</TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          เลขที่Batch
                        </TableCell>
                        <TableCell sx={cellStyle}>ซัพพลายเออร์</TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          น้ำหนัก
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          ราคาต่อหน่วย
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          รวมก่อนภาษี (บาท)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedDetails[selectedItemKey].map((item, index) => (
                        <TableRow key={index}>
                          <TableCell sx={cellStyle}>
                            {item.poDate &&
                              new Date(item.poDate).toLocaleString("th-TH", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              }) + " น."}
                          </TableCell>

                          <TableCell align="right" sx={cellStyle}>
                            {item.poNumber}
                          </TableCell>
                          <TableCell sx={cellStyle}>{item.supplier}</TableCell>
                          <TableCell align="right" sx={cellStyle}>
                            {formatNum(item.netWeight)}
                          </TableCell>
                          <TableCell align="right" sx={cellStyle}>
                            {formatNum(item.pricePerUnit)}
                          </TableCell>
                          <TableCell align="right" sx={cellStyle}>
                            {formatNum(
                              item.amountBeforeVat ??
                                (item.netWeight || 0) * (item.pricePerUnit || 0)
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setModalOpen(false)}>ปิด</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}
